import { Check, EllipsisVertical, Loader2 } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import DeleteChatConfirmationModal from "~/components/DeleteChatConfirmationModal";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Separator } from "~/components/ui/separator";
import { SidebarTrigger } from "~/components/ui/sidebar";
import { useAuth } from "~/context/AuthContext";
import { useBreadcrumb } from "~/context/BreadcrumbContext";
import { getBreadcrumbSpec } from "~/lib/breadcrumb-routes";
import { Link, useLocation } from "~/lib/team-router";
import { cn } from "~/lib/utils";

interface Conversation {
  id: string;
  title: string;
  created_at: string;
}

interface NavigationHeaderProps {
  conversations: Conversation[];
}

export default function NavigationHeader({
  conversations,
}: NavigationHeaderProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { pageTitle, pageAction, pageHeaderContent } = useBreadcrumb();

  function getConversationTitle(conversationId: string) {
    const conversation = conversations.find(
      (conversation) => conversation.id == conversationId
    );
    return conversation?.title || "";
  }

  const spec = getBreadcrumbSpec(location.pathname, {
    isAgency: Boolean(user?.agency),
  });

  const breadcrumbItems: ReactNode[] = [];

  if (spec) {
    const withDynamicTail = Boolean(
      spec.appendPageTitle || spec.appendChatTitle
    );

    spec.segments.forEach((seg, index) => {
      const isLastSegment = index === spec.segments.length - 1;
      const showAsLink = Boolean(seg.to) && (withDynamicTail || !isLastSegment);

      if (showAsLink && seg.to) {
        breadcrumbItems.push(
          <BreadcrumbItem
            key={`crumb-${index}-${seg.label}`}
            className="hidden shrink-0 md:block"
          >
            <BreadcrumbLink asChild>
              <Link to={seg.to}>{seg.label}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
        );
      } else {
        breadcrumbItems.push(
          <BreadcrumbItem
            key={`crumb-${index}-${seg.label}`}
            className="hidden shrink-0 md:block"
          >
            <BreadcrumbPage className="block max-w-[200px] truncate">
              {seg.label}
            </BreadcrumbPage>
          </BreadcrumbItem>
        );
      }
    });

    if (spec.appendPageTitle && pageTitle) {
      breadcrumbItems.push(
        <BreadcrumbItem
          key="page-title"
          className="hidden min-w-0 flex-1 overflow-hidden md:flex"
        >
          <BreadcrumbPage className="block w-full truncate" title={pageTitle}>
            {pageTitle}
          </BreadcrumbPage>
        </BreadcrumbItem>
      );
    }

    if (spec.appendChatTitle) {
      const chatId = location.pathname.split("/")[2] ?? "";
      const chatTitleText = getConversationTitle(chatId) || "New Chat";
      breadcrumbItems.push(
        <BreadcrumbItem
          key="chat-title"
          className="hidden min-w-0 flex-1 overflow-hidden md:flex"
        >
          <BreadcrumbPage
            className="block w-full truncate"
            title={chatTitleText}
          >
            {chatTitleText}
          </BreadcrumbPage>
        </BreadcrumbItem>
      );
    }
  }

  return (
    <header
      className={`flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 ${
        location.pathname.startsWith("/settings") ||
        location.pathname === "/profile"
          ? "bg-white"
          : ""
      }`}
    >
      <div className="flex min-w-0 w-full items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1 shrink-0" />
        <Separator
          orientation="vertical"
          className="mr-2 hidden h-4 shrink-0 md:block"
        />
        <Breadcrumb className="hidden min-w-0 flex-1 overflow-hidden md:block">
          <BreadcrumbList className="min-w-0 flex-nowrap overflow-hidden">
            {breadcrumbItems.map((item, index) => (
              <Fragment key={index}>
                {index > 0 && (
                  <BreadcrumbSeparator className="hidden md:block" />
                )}
                {item}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-stone-800 md:hidden">
          {pageTitle ||
            spec?.segments[spec.segments.length - 1]?.label ||
            "AI Visibility"}
        </p>
        {pageHeaderContent ? (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {pageHeaderContent}
          </div>
        ) : pageAction ? (
          <div className="ml-auto shrink-0 flex items-center">
            {pageAction.variant === "success" || pageAction.variant === "ghost" ? (
              <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-500 select-none", pageAction.className)}>
                {pageAction.loading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-500" />
                ) : (
                  pageAction.icon || <Check className="h-4 w-4 text-emerald-500 stroke-[2.5]" />
                )}
                <span>{pageAction.label}</span>
              </span>
            ) : (
              <Button
                type="button"
                size="sm"
                className={cn("h-7 shrink-0 gap-1 px-2.5 text-xs bg-orange-500 hover:bg-orange-600 text-white", pageAction.className)}
                disabled={pageAction.disabled}
                onClick={pageAction.onClick}
              >
                {pageAction.loading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    {pageAction.icon}
                    {pageAction.label}
                  </>
                )}
              </Button>
            )}
          </div>
        ) : /^\/chats\/\d+$/.test(location.pathname) ? (
          <div className="ml-auto shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center justify-center">
                <div className="cursor-pointer p-1.5 rounded-md hover:bg-gray-100">
                  <EllipsisVertical className="h-4 w-4" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <DeleteChatConfirmationModal />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>
    </header>
  );
}
